import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Plus, Megaphone, ChevronRight } from 'lucide-react';
import ContractGatePrompt from '../components/ContractGatePrompt';
import {
  CONTRACT_GATE_MESSAGE,
  requiresContractForPublishing,
} from '../lib/contractGate';
import { isCampaignPastEndDate } from '@/lib/campaigns';

interface Campaign {
  id: string;
  title: string;
  description: string;
  goal_slices: number;
  total_slices_raised: number;
  backer_count: number;
  status: string;
  end_date: string | null;
  created_at: string;
  work_id: string | null;
  works: { title: string } | null;
}

function daysRemaining(endDate: string | null): string {
  if (!endDate) return '';
  const diff = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'Ended';
  if (diff === 0) return 'Ends today';
  return `${diff} day${diff !== 1 ? 's' : ''} remaining`;
}

function fmt(n: number) {
  return n.toLocaleString();
}

export default function Campaigns() {
  const { publisher } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publisher?.id) fetchCampaigns();
  }, [publisher?.id]);

  const fetchCampaigns = async () => {
    if (!publisher?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crowdfunding_campaigns')
        .select('*, works(title)')
        .eq('publisher_id', publisher.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      let loaded = (data as Campaign[]) || [];

      const expiredIds = loaded
        .filter(c => c.status === 'active' && isCampaignPastEndDate(c.end_date))
        .map(c => c.id);

      if (expiredIds.length > 0) {
        const { error: closeError } = await supabase
          .from('crowdfunding_campaigns')
          .update({ status: 'completed' })
          .in('id', expiredIds);
        if (!closeError) {
          loaded = loaded.map(c =>
            expiredIds.includes(c.id) ? { ...c, status: 'completed' } : c
          );
        }
      }

      setCampaigns(loaded);
    } catch (err: any) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const contractBlocked = requiresContractForPublishing(publisher);

  const handleActivate = async (campaignId: string) => {
    if (contractBlocked) {
      toast.error(CONTRACT_GATE_MESSAGE);
      return;
    }
    try {
      const { error } = await supabase
        .from('crowdfunding_campaigns')
        .update({ status: 'active' })
        .eq('id', campaignId);
      if (error) throw error;
      setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, status: 'active' } : c));
      toast.success('Campaign activated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to activate campaign');
    }
  };

  const active = campaigns.filter(c => c.status === 'active');
  const drafts = campaigns.filter(c => c.status === 'draft');
  const closed = campaigns.filter(c => c.status === 'completed' || c.status === 'cancelled');

  const CampaignCard = ({ campaign }: { campaign: Campaign }) => {
    const pct = campaign.goal_slices > 0
      ? Math.min(100, Math.round((campaign.total_slices_raised / campaign.goal_slices) * 100))
      : 0;

    return (
      <Card className="partner-card hover:border-primary/40 transition-colors">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base truncate">{campaign.title}</CardTitle>
              {campaign.works?.title && (
                <CardDescription className="mt-0.5">
                  Linked to: {campaign.works.title}
                </CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                variant="outline"
                className={
                  campaign.status === 'active' ? 'text-green-500 border-green-500/30' :
                  campaign.status === 'draft' ? 'text-yellow-500 border-yellow-500/30' :
                  campaign.status === 'completed' ? 'text-blue-500 border-blue-500/30' :
                  'text-muted-foreground'
                }
              >
                {campaign.status}
              </Badge>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/campaigns/${campaign.id}`}>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{fmt(campaign.total_slices_raised)} / {fmt(campaign.goal_slices)} slices</span>
              <span>{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{fmt(campaign.backer_count)} backer{campaign.backer_count !== 1 ? 's' : ''}</span>
            {campaign.end_date && <span>{daysRemaining(campaign.end_date)}</span>}
          </div>
          {campaign.status === 'draft' && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={contractBlocked}
              onClick={() => handleActivate(campaign.id)}
            >
              Activate Campaign
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Crowdfunding Campaigns</h1>
          <p className="text-muted-foreground mt-1">Manage your fundraising campaigns</p>
        </div>
        <Button onClick={() => navigate('/campaigns/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {contractBlocked && <ContractGatePrompt compact />}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : campaigns.length === 0 ? (
        <Card className="partner-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Megaphone className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">No campaigns yet</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Start a crowdfunding campaign to fund your next project.
              </p>
            </div>
            <Button onClick={() => navigate('/campaigns/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Active</h2>
              <div className="grid gap-4 md:grid-cols-2">{active.map(c => <CampaignCard key={c.id} campaign={c} />)}</div>
            </section>
          )}
          {drafts.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Drafts</h2>
              <div className="grid gap-4 md:grid-cols-2">{drafts.map(c => <CampaignCard key={c.id} campaign={c} />)}</div>
            </section>
          )}
          {closed.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Completed / Cancelled</h2>
              <div className="grid gap-4 md:grid-cols-2">{closed.map(c => <CampaignCard key={c.id} campaign={c} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
